import React, {useState} from 'react';
import useApi from "../../hooks/useApi/useApi";
import {Button, Form, Stack} from "../ui";
import {IFile, ITemplate} from "../../ModelContracts";
import {useMutation, useQuery, useQueryClient} from "react-query";
import Labels from "../Labels/Labels";
import {useSearchParams} from "react-router-dom";
import EditTemplate from "../EditTemplate/EditTemplate";
import FileList from "../FileList/FileList";

import "./ViewTemplate.css";

interface IProps {
	id: string
}

function ViewTemplate({id}: IProps) {
	const [searchParams, setSearchParams] = useSearchParams();
	const [template, setTemplate] = useState<ITemplate | null>();
	const {getTemplate, removeTemplateFile} = useApi();
	const queryClient = useQueryClient();
	useQuery<ITemplate>(['template', id], () => getTemplate(id), {
		enabled: !!id,
		onSuccess: (data: ITemplate) => {
			setTemplate(data);
		}
	});
	const updateOptions = {
		onSuccess: async () => {
			await queryClient.invalidateQueries('template');
			await queryClient.invalidateQueries('templates');
		}
	};
	const removeTemplateFileCommand =
		useMutation((fileId: string) => removeTemplateFile(id, fileId), updateOptions);

	const handleEdit = () => {
		const params = searchParams;
		params.append('edit', 'true');
		setSearchParams(params);
	}

	const handleFileDeleted = (file: IFile) => {
		removeTemplateFileCommand.mutate(file.id);
	};

	const handlePreview = () => {
		const params = searchParams;
		params.delete('edit');
		setSearchParams(params);
	}

	const isEditMode = () => {
		const param = searchParams.get('edit');
		if (!param) return false;
		return param.toLowerCase() === 'true'
	};

	if (!template) return <></>;
	return (
		<div className="view-template px-4 pb-12" aria-label="View Template">
			<div className="flex justify-between items-center">
				<h2 className="my-2">{template.name}</h2>
				{
					!isEditMode() &&
					<Button variant="outline-primary" size="sm" onClick={handleEdit} title="Edit Template">
						<i className="bi bi-pencil mr-1"></i>
						Edit
					</Button>
				}
				{
					isEditMode() &&
					<Button variant="outline-primary" size="sm" onClick={handlePreview} title="Preview Task">
						<i className="bi bi-binoculars mr-1"></i>
						Preview
					</Button>
				}
			</div>
			{
				!isEditMode() &&
				<>
					<div>
						{
							template.description &&
							<p className="description mt-6"
							   dangerouslySetInnerHTML={{__html: template.description}}></p>
						}
						{
							!template.description &&
							<p className="italic mt-6">No description set on template.</p>
						}
					</div>
					<div className="labels-container bg-base-200 w-full w-10/12 py-2">
						<Labels labels={template.labels}/>
					</div>
				</>
			}
			{
				isEditMode() && <EditTemplate template={template} />
			}
			<div className="mt-2">
				<FileList files={template.files} onDeleted={handleFileDeleted} allowDelete={isEditMode()} />
			</div>
		</div>
	);
}

export default ViewTemplate;
